import { useEffect, useRef, useCallback } from 'react';

/**
 * CalendarNotifications - Background component that monitors calendar events
 * and shows browser notifications when reminders are due.
 */
const CalendarNotifications = () => {
  const shownNotifications = useRef(new Set());
  const permissionGranted = useRef(false);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      permissionGranted.current = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      permissionGranted.current = permission === 'granted';
      return permissionGranted.current;
    }

    return false;
  }, []);

  // Show a notification
  const showNotification = useCallback((event, minutesUntil) => {
    if (!permissionGranted.current) return;

    const notificationId = `${event.id}-${minutesUntil}`;

    // Don't show the same notification twice
    if (shownNotifications.current.has(notificationId)) return;
    shownNotifications.current.add(notificationId);

    const timeText = minutesUntil <= 0
      ? 'now'
      : minutesUntil === 1
        ? 'in 1 minute'
        : `in ${minutesUntil} minutes`;

    const notification = new Notification(`${event.title} - ${timeText}`, {
      body: event.location ? `Location: ${event.location}` : 'Click to view event',
      icon: '/favicon.ico',
      tag: notificationId,
      requireInteraction: minutesUntil <= 5, // Keep on screen if event is very soon
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto-close after 30 seconds if not clicked
    setTimeout(() => notification.close(), 30000);
  }, []);

  // Check for upcoming events with reminders
  const checkReminders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Fetch events for the next 24 hours
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      const response = await fetch(
        `/api/calendar/events?start_date=${now.toISOString()}&end_date=${tomorrow.toISOString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) return;

      const events = await response.json();

      events.forEach(event => {
        const eventStart = new Date(event.start_time);
        const minutesUntilEvent = Math.floor((eventStart - now) / (1000 * 60));

        // Check if any reminder should fire
        const reminders = event.reminders || [];

        // Default reminder at 15 minutes if none specified
        const reminderTimes = reminders.length > 0
          ? reminders.map(r => r.minutes_before)
          : [15];

        reminderTimes.forEach(reminderMinutes => {
          // Show notification if we're within 1 minute of the reminder time
          // or if the event is happening now
          if (minutesUntilEvent <= reminderMinutes && minutesUntilEvent >= reminderMinutes - 1) {
            showNotification(event, minutesUntilEvent);
          }

          // Also notify when event is starting (0 minutes)
          if (minutesUntilEvent <= 0 && minutesUntilEvent >= -1) {
            showNotification(event, 0);
          }
        });
      });
    } catch (error) {
      console.error('Error checking calendar reminders:', error);
    }
  }, [showNotification]);

  // Initial setup and periodic checking
  useEffect(() => {
    // Request permission on mount
    requestPermission();

    // Check immediately
    checkReminders();

    // Check every minute
    const interval = setInterval(checkReminders, 60000);

    // Clean up old notification IDs every hour to prevent memory buildup
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      // Keep IDs for 2 hours
      shownNotifications.current = new Set(
        [...shownNotifications.current].filter(id => {
          const timestamp = parseInt(id.split('-').pop()) || 0;
          return now - timestamp < 2 * 60 * 60 * 1000;
        })
      );
    }, 60 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearInterval(cleanupInterval);
    };
  }, [requestPermission, checkReminders]);

  // This component doesn't render anything
  return null;
};

export default CalendarNotifications;
